import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import {
  ApiRequestDataType,
  ApiRequestStatus,
  StoredErrorResponseType,
} from '../../../types/api/api.types'
import { SavedContact } from '../../../services/api/saved-contacts.service'
import {
  getSavedContactsThunk,
  getSavedContactThunk,
  createSavedContactThunk,
  updateSavedContactThunk,
  deleteSavedContactThunk,
  incrementContactUsageThunk,
  findSavedContactThunk,
} from '../thunks/saved-contacts.thunk'

export type SavedContactsState = {
  contacts: SavedContact[]
  selectedContact: SavedContact | null
  isError: boolean
  isSuccess: boolean
  isLoading: boolean
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  requestResponse: ApiRequestDataType<any>
}

const initialState: SavedContactsState = {
  contacts: [],
  selectedContact: null,
  isError: false,
  isSuccess: false,
  isLoading: false,
  requestResponse: {
    status: ApiRequestStatus.IDLE,
    data: null,
    error: undefined,
    success: false,
  },
}

const savedContactsSlice = createSlice({
  name: 'savedContacts',
  initialState,
  reducers: {
    resetSavedContactsState: (state) => {
      state.contacts = []
      state.selectedContact = null
      state.isError = false
      state.isSuccess = false
      state.isLoading = false
    },
    resetSavedContactsReqRes: (state) => {
      state.requestResponse.status = ApiRequestStatus.IDLE
      state.requestResponse.data = null
      state.requestResponse.success = false
    },
    setSelectedContact: (state, action: PayloadAction<SavedContact | null>) => {
      state.selectedContact = action.payload
    },
  },
  extraReducers: (builder) => {
    builder
      // Get all contacts
      .addCase(getSavedContactsThunk.pending, (state) => {
        state.requestResponse.status = ApiRequestStatus.PENDING
        state.isLoading = true
        state.requestResponse.error = {} as StoredErrorResponseType
      })
      .addCase(getSavedContactsThunk.fulfilled, (state, action) => {
        state.requestResponse.status = ApiRequestStatus.FULFILLED
        state.requestResponse.data = action.payload
        state.contacts = action.payload
        state.isLoading = false
        state.isSuccess = true
      })
      .addCase(getSavedContactsThunk.rejected, (state, action) => {
        state.requestResponse.status = ApiRequestStatus.REJECTED
        state.requestResponse.error = action.payload as StoredErrorResponseType
        state.isLoading = false
        state.isError = true
      })

      // Get single contact
      .addCase(getSavedContactThunk.pending, (state) => {
        state.requestResponse.status = ApiRequestStatus.PENDING
        state.isLoading = true
      })
      .addCase(getSavedContactThunk.fulfilled, (state, action) => {
        state.requestResponse.status = ApiRequestStatus.FULFILLED
        state.selectedContact = action.payload
        state.isLoading = false
        state.isSuccess = true
      })
      .addCase(getSavedContactThunk.rejected, (state, action) => {
        state.requestResponse.status = ApiRequestStatus.REJECTED
        state.requestResponse.error = action.payload as StoredErrorResponseType
        state.isLoading = false
        state.isError = true
      })

      // Create contact
      .addCase(createSavedContactThunk.pending, (state) => {
        state.requestResponse.status = ApiRequestStatus.PENDING
        state.isLoading = true
        state.requestResponse.error = {} as StoredErrorResponseType
      })
      .addCase(createSavedContactThunk.fulfilled, (state, action) => {
        state.requestResponse.status = ApiRequestStatus.FULFILLED
        state.requestResponse.data = action.payload
        state.requestResponse.success = true
        // Add new contact to the list
        state.contacts.unshift(action.payload)
        state.isLoading = false
        state.isSuccess = true
      })
      .addCase(createSavedContactThunk.rejected, (state, action) => {
        state.requestResponse.status = ApiRequestStatus.REJECTED
        state.requestResponse.error = action.payload as StoredErrorResponseType
        state.requestResponse.success = false
        state.isLoading = false
        state.isError = true
      })

      // Update contact
      .addCase(updateSavedContactThunk.pending, (state) => {
        state.requestResponse.status = ApiRequestStatus.PENDING
        state.isLoading = true
      })
      .addCase(updateSavedContactThunk.fulfilled, (state, action) => {
        state.requestResponse.status = ApiRequestStatus.FULFILLED
        state.requestResponse.data = action.payload
        state.requestResponse.success = true
        // Update contact in the list
        const index = state.contacts.findIndex((c) => c.id === action.payload.id)
        if (index !== -1) {
          state.contacts[index] = action.payload
        }
        state.isLoading = false
        state.isSuccess = true
      })
      .addCase(updateSavedContactThunk.rejected, (state, action) => {
        state.requestResponse.status = ApiRequestStatus.REJECTED
        state.requestResponse.error = action.payload as StoredErrorResponseType
        state.requestResponse.success = false
        state.isLoading = false
        state.isError = true
      })

      // Delete contact
      .addCase(deleteSavedContactThunk.pending, (state) => {
        state.requestResponse.status = ApiRequestStatus.PENDING
        state.isLoading = true
      })
      .addCase(deleteSavedContactThunk.fulfilled, (state, action) => {
        state.requestResponse.status = ApiRequestStatus.FULFILLED
        state.requestResponse.success = true
        // Remove contact from the list (action.meta.arg contains the contactId)
        state.contacts = state.contacts.filter((c) => c.id !== action.meta.arg.contactId)
        state.isLoading = false
        state.isSuccess = true
      })
      .addCase(deleteSavedContactThunk.rejected, (state, action) => {
        state.requestResponse.status = ApiRequestStatus.REJECTED
        state.requestResponse.error = action.payload as StoredErrorResponseType
        state.requestResponse.success = false
        state.isLoading = false
        state.isError = true
      })

      // Increment usage
      .addCase(incrementContactUsageThunk.pending, (state) => {
        state.requestResponse.status = ApiRequestStatus.PENDING
      })
      .addCase(incrementContactUsageThunk.fulfilled, (state, action) => {
        state.requestResponse.status = ApiRequestStatus.FULFILLED
        state.requestResponse.data = action.payload
        // Update usage_count and last_used for the contact
        const index = state.contacts.findIndex((c) => c.id === action.payload.id)
        if (index !== -1) {
          state.contacts[index] = action.payload
        }
      })
      .addCase(incrementContactUsageThunk.rejected, (state, action) => {
        state.requestResponse.status = ApiRequestStatus.REJECTED
        state.requestResponse.error = action.payload as StoredErrorResponseType
      })

      // Find contact
      .addCase(findSavedContactThunk.pending, (state) => {
        state.requestResponse.status = ApiRequestStatus.PENDING
        state.isLoading = true
      })
      .addCase(findSavedContactThunk.fulfilled, (state, action) => {
        state.requestResponse.status = ApiRequestStatus.FULFILLED
        state.selectedContact = action.payload
        state.isLoading = false
      })
      .addCase(findSavedContactThunk.rejected, (state, action) => {
        state.requestResponse.status = ApiRequestStatus.REJECTED
        state.requestResponse.error = action.payload as StoredErrorResponseType
        state.isLoading = false
      })
  },
})

export const { resetSavedContactsState, resetSavedContactsReqRes, setSelectedContact } =
  savedContactsSlice.actions

export default savedContactsSlice.reducer
